import { getAmbientScribeStatus } from "features/medical-scribe/utils";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { API_TAGS, emptySplitApi } from "./empty-split-api";
import type {
  AmbientScribeProcessingContext,
  AmbientScribeProcessingContextApi
} from "./types/ambient-scribe-processing-context.type";
import type {
  AmbientScribeTemplate,
  AmbientScribeTemplateCreateInput,
  AmbientScribeTemplateFilters,
  AmbientScribeTemplateUpdateInput
} from "./types/ambient-scribe-template.type";
import type {
  AmbientScribe,
  AmbientScribeFeedbackRequest,
  AmbientScribeFeedbackResponse,
  AmbientScribeFilters,
  AmbientScribeInput,
  AmbientScribeUpdateInput,
  ScribeAudioChunk,
  ScribeRecordingSession
} from "./types/ambient-scribe.type";
import type { ProcessingEvent } from "./types/processing-event.type";
import type { PromptOutputFormat } from "./types/prompts.type";

/** Args for `getProcessingEvents`. */
export type GetProcessingEventsQueryArg = {
  scribeId: number;
  offset?: number;
  limit?: number;
};

/** Paginated DRF-style response from `GET .../processing-events/`. */
export type GetProcessingEventsResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProcessingEvent[];
};

/** Raw API payload: DRF page or a bare list (legacy / alternate BE shape). */
export type GetProcessingEventsApiPayload =
  | GetProcessingEventsResponse
  | ProcessingEvent[];

function normalizeProcessingEventsResponse(
  response: GetProcessingEventsApiPayload,
  arg: GetProcessingEventsQueryArg
): GetProcessingEventsResponse {
  if (Array.isArray(response)) {
    const offset = arg.offset ?? 0;
    const limit = arg.limit ?? 25;
    const results = response.slice(offset, offset + limit);
    const loadedThrough = offset + results.length;
    return {
      count: response.length,
      next: loadedThrough < response.length ? "__has_more__" : null,
      previous: offset > 0 ? "__has_prev__" : null,
      results
    };
  }
  return response;
}

function deriveHasTranscriptionFromProcessingContextTranscription(
  transcription: unknown | null
): boolean {
  if (transcription == null || typeof transcription !== "object") {
    return false;
  }
  const channels = (transcription as { channels?: unknown }).channels;
  if (!Array.isArray(channels) || channels.length === 0) {
    return false;
  }
  const channel0 = channels[0];
  if (channel0 == null || typeof channel0 !== "object") {
    return false;
  }
  const alternatives = (channel0 as { alternatives?: unknown }).alternatives;
  if (!Array.isArray(alternatives) || alternatives.length === 0) {
    return false;
  }
  const alt0 = alternatives[0];
  if (alt0 == null || typeof alt0 !== "object") {
    return false;
  }
  const transcript = (alt0 as { transcript?: unknown }).transcript;
  return typeof transcript === "string" && transcript.trim() !== "";
}

function transformAmbientScribeProcessingContextResponse(
  response: AmbientScribeProcessingContextApi
): AmbientScribeProcessingContext {
  const backendStatus = response.status;
  const has_transcription =
    deriveHasTranscriptionFromProcessingContextTranscription(
      response.transcription
    );
  const status = getAmbientScribeStatus({
    id: response.id,
    status: backendStatus,
    has_transcription
  });
  return {
    ...response,
    backend_status: backendStatus,
    has_transcription,
    status
  };
}

type ApiTagType = (typeof API_TAGS)[number];
const AMBIENT_SCRIBE_TEMPLATES_TAG = "AmbientScribeTemplates" as ApiTagType;
const AUDIO_CHUNKS_TAG = "AmbientScribeAudioChunks" as ApiTagType;

export const DEFAULT_AMBIENT_SCRIBES_QUERY_NON_STREAMING =
  "{id,created_at,updated_at,duration,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active}}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY_DETAILED_NON_STREAMING =
  "{id,created_at,updated_at,duration,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},summary,soap_notes,transcription}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY =
  "{id,created_at,updated_at,status,duration,is_generating_summary,is_generating_soap,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active}}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY_DETAILED =
  "{id,created_at,updated_at,status,duration,is_generating_summary,is_generating_soap,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},summary,soap_notes,transcription}";

export const DEFAULT_AMBIENT_SCRIBE_QUERY_NON_STREAMING =
  "{id,summary,soap_notes,has_transcription,transcription,created_at,updated_at,duration,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active}}";

export const DEFAULT_AMBIENT_SCRIBE_QUERY =
  "{id,summary,soap_notes,has_transcription,transcription,is_generating_summary,is_generating_soap,status,created_at,updated_at,duration,recording,custom_context,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},clinic{id,name,timezone}}";

export const DEFAULT_AMBIENT_SCRIBE_QUERY_WITH_TEMPLATE =
  "{id,summary,soap_notes,has_transcription,transcription,is_generating_summary,is_generating_soap,status,created_at,updated_at,duration,recording,custom_context,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},clinic{id,name,timezone},ambient_scribe_template_id}";

// Paused scribe edit (SN-17727): adds custom_context + chart_note for the Edit Scribe tab and
// resume-metadata. Scoped to the paused-edit UI so other callers' payloads are unchanged.
export const DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED =
  "{id,summary,soap_notes,has_transcription,transcription,is_generating_summary,is_generating_soap,status,created_at,updated_at,duration,recording,custom_context,chart_note,ambient_scribe_template_id,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},clinic{id,name,timezone}}";

export const ambientScribeApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAmbientScribe: builder.query<
      AmbientScribe,
      { id: number; query?: string }
    >({
      query: ({ id, query = DEFAULT_AMBIENT_SCRIBE_QUERY }) => ({
        url: `/ai/ambient-scribes/${id}/?query=${query}`
      }),
      transformResponse: (response: AmbientScribe) => {
        return { ...response, status: getAmbientScribeStatus(response) };
      },
      providesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.id }
      ]
    }),

    getAmbientScribes: builder.query<AmbientScribe[], AmbientScribeFilters>({
      query: ({
        offset,
        limit = 25,
        query = DEFAULT_AMBIENT_SCRIBES_QUERY,
        ...params
      }) => {
        return {
          url: `/ai/ambient-scribes/?offset=${
            offset || 0
          }&limit=${limit}&query=${query}&${formatValidQueryParamUrlCommaSeparated(
            params
          )}`,
          method: "GET"
        };
      },
      providesTags: ["AmbientScribes"],
      transformResponse: (
        response:
          | AmbientScribe[]
          | {
              count: number;
              next: string;
              previous: string;
              results: AmbientScribe[];
            }
      ) => {
        let result: AmbientScribe[] = [];
        if ("count" in response) {
          result = response.results;
        } else {
          result = response;
        }
        return result.map((scribe) => ({
          ...scribe,
          status: getAmbientScribeStatus(scribe)
        }));
      }
    }),

    getAmbientScribesPaginated: builder.query<
      { results: AmbientScribe[]; count: number },
      AmbientScribeFilters & { offset: number; limit: number }
    >({
      query: ({
        offset = 0,
        limit = 25,
        query = DEFAULT_AMBIENT_SCRIBES_QUERY,
        ...params
      }) => ({
        url: `/ai/ambient-scribes/?offset=${offset}&limit=${limit}&query=${query}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}`,
        method: "GET"
      }),
      providesTags: ["AmbientScribes"],
      transformResponse: (
        response:
          | AmbientScribe[]
          | {
              count: number;
              next: string;
              previous: string;
              results: AmbientScribe[];
            }
      ) => {
        let results: AmbientScribe[] = [];
        let count = 0;
        if ("count" in response) {
          results = response.results;
          count = response.count;
        } else {
          results = Array.isArray(response) ? response : [];
          count = results.length;
        }
        return {
          results: results.map((scribe) => ({
            ...scribe,
            status: getAmbientScribeStatus(scribe)
          })),
          count
        };
      }
    }),

    createAmbientScribe: builder.mutation<AmbientScribe, AmbientScribeInput>({
      query: ({ patient, duration, staff, practice, audio_file, clinic }) => {
        const formData = new FormData();
        formData.append("patient", String(patient));
        formData.append("duration", String(duration));
        formData.append("staff", String(staff));
        formData.append("practice", String(practice));
        if (clinic) {
          formData.append("clinic", String(clinic));
        }
        formData.append("audio_file", audio_file);
        formData.append("auto_transcribe", "true");

        return {
          url: "/audio-transcription/",
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    updateAmbientScribe: builder.mutation<
      AmbientScribe,
      AmbientScribeUpdateInput
    >({
      query: ({ id, skipCacheInvalidation, ...body }) => {
        return {
          url: `/ai/ambient-scribes/${id}/`,
          method: "PATCH",
          body
        };
      },
      invalidatesTags: (result, error, arg) => {
        if (error || arg.skipCacheInvalidation) return [];
        return [
          { type: "AmbientScribes", id: arg.id },
          "AmbientScribes",
          "AmbientScribeProcessingEvents"
        ];
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        // skipCacheInvalidation: autosave patches getAmbientScribe detail caches only —
        // avoids list refetch churn while typing. List views refresh on explicit actions
        // (complete, pause, notifyScribeRefetch, etc.).
        if (!arg.skipCacheInvalidation) return;

        try {
          const { data } = await queryFulfilled;
          const cachedArgs = ambientScribeApi.util.selectCachedArgsForQuery(
            getState(),
            "getAmbientScribe"
          );
          const patch: Partial<AmbientScribe> = {};
          if (arg.custom_context !== undefined) {
            patch.custom_context =
              data.custom_context ?? arg.custom_context ?? null;
          }
          if (data.updated_at) {
            patch.updated_at = data.updated_at;
          }
          if (Object.keys(patch).length === 0) return;

          for (const originalArgs of cachedArgs) {
            if (originalArgs.id !== arg.id) continue;
            dispatch(
              ambientScribeApi.util.updateQueryData(
                "getAmbientScribe",
                originalArgs,
                (draft) => {
                  Object.assign(draft, patch);
                }
              )
            );
          }
        } catch {
          // no-op — failed PATCH should not patch cache
        }
      }
    }),

    generateAmbientSummary: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/ai/ambient-scribes/${id}/transform/`,
          method: "POST",
          body: {
            transform_type: "summary"
          }
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),
    generateAmbientSoapNotes: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/ai/ambient-scribes/${id}/transform/`,
          method: "POST",
          body: {
            transform_type: "soap"
          }
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    generateAmbientScribeAsync: builder.mutation<
      void,
      { scribe_id: number; ambient_scribe_template_id?: number }
    >({
      query: ({ scribe_id, ambient_scribe_template_id }) => {
        const body: {
          transform_type: "soap";
          format: "html";
          ambient_scribe_template_id?: number;
        } = {
          transform_type: "soap",
          format: "html"
        };
        if (ambient_scribe_template_id != null) {
          body.ambient_scribe_template_id = ambient_scribe_template_id;
        }
        return {
          url: `/ai/ambient-scribes/${scribe_id}/async/`,
          method: "POST",
          body
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        "AmbientScribeProcessingEvents"
      ]
    }),

    generateTranscription: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/ai/ambient-scribes/${id}/transform/`,
          method: "POST",
          body: {
            transform_type: "transcribe"
          }
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    deleteAmbientScribe: builder.mutation<AmbientScribe, number>({
      query: (id) => {
        return {
          url: `/ai/ambient-scribes/${id}/`,
          method: "DELETE"
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    getAmbientScribeFeedback: builder.query<
      AmbientScribeFeedbackResponse[],
      number
    >({
      query: (id) => ({
        url: `/ai/ambient-scribes/${id}/feedback/`
      }),
      providesTags: ["AmbientScribeFeedback"]
    }),

    submitAmbientScribeFeedback: builder.mutation<
      AmbientScribeFeedbackResponse,
      AmbientScribeFeedbackRequest & { scribe_id: number }
    >({
      query: ({ scribe_id, ...body }) => {
        return {
          url: `/ai/ambient-scribes/${scribe_id}/feedback/`,
          method: "POST",
          body: {
            ...body
          }
        };
      },
      invalidatesTags: [
        "AmbientScribeFeedback",
        "AmbientScribeProcessingEvents"
      ]
    }),

    finalizeStuckScribe: builder.mutation<void, { scribe_id: number }>({
      query: ({ scribe_id }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/finalize/`,
        method: "POST"
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        "AmbientScribeProcessingEvents",
        "AmbientScribeSessions"
      ]
    }),

    // Mark an already session-paused scribe as Paused status (resumable, not finalized).
    // BE route: POST .../pause/ (SN-17727). FE tears down widget after 2xx.
    pauseAmbientScribe: builder.mutation<
      AmbientScribe,
      { scribe_id: number; duration?: number }
    >({
      query: ({ scribe_id, duration }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/pause/`,
        method: "POST",
        body: duration != null ? { duration } : {}
      }),
      transformResponse: (response: AmbientScribe) => ({
        ...response,
        status: getAmbientScribeStatus(response)
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes"
      ]
    }),

    getScribeRecordingSessions: builder.query<
      ScribeRecordingSession[],
      { scribeId: number }
    >({
      query: ({ scribeId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/recording-sessions/`
      }),
      transformResponse: (
        response:
          | ScribeRecordingSession[]
          | { results: ScribeRecordingSession[] }
      ) => (Array.isArray(response) ? response : (response.results ?? [])),
      providesTags: (result, error, arg) => [
        "AmbientScribeSessions",
        { type: "AmbientScribes", id: arg.scribeId }
      ]
    }),

    deleteScribeRecordingSession: builder.mutation<
      void,
      { scribeId: number; sessionId: number }
    >({
      query: ({ scribeId, sessionId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/recording-sessions/${sessionId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, arg) => [
        "AmbientScribeSessions",
        { type: "AmbientScribes", id: arg.scribeId },
        "AmbientScribeProcessingEvents"
      ]
    }),

    // Revert a COMPLETED/FAILED/CANCELLED scribe to Paused so audio chunks
    // can be deleted and the scribe re-completed (SN-17727).
    editAmbientScribe: builder.mutation<AmbientScribe, { scribe_id: number }>({
      query: ({ scribe_id }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/edit/`,
        method: "POST"
      }),
      transformResponse: (response: AmbientScribe) => ({
        ...response,
        status: getAmbientScribeStatus(response)
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        AUDIO_CHUNKS_TAG
      ]
    }),

    // Trigger full transcription → SOAP pipeline for a Paused scribe (SN-17727).
    completeAmbientScribe: builder.mutation<
      {
        success: boolean;
        message?: string;
        already_complete?: boolean;
        cancelled?: boolean;
        reason?: string;
        status?: number;
      },
      { scribe_id: number }
    >({
      query: ({ scribe_id }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/complete/`,
        method: "POST"
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        "AmbientScribeSessions",
        AUDIO_CHUNKS_TAG
      ]
    }),

    // List audio chunks for a scribe — richer than recording-sessions: includes
    // presigned audio URL and per-chunk transcription_chunks (SN-17727).
    getScribeAudioChunks: builder.query<
      ScribeAudioChunk[],
      { scribeId: number }
    >({
      query: ({ scribeId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/audio-chunks/`
      }),
      transformResponse: (
        response: ScribeAudioChunk[] | { results: ScribeAudioChunk[] }
      ) => (Array.isArray(response) ? response : (response.results ?? [])),
      providesTags: (result, error, arg) => [
        AUDIO_CHUNKS_TAG,
        { type: "AmbientScribes", id: arg.scribeId }
      ]
    }),

    // Delete an audio chunk (and its linked session). Use when chunk.id !== null.
    // For transcript-only chunks (id === null), use deleteScribeRecordingSession.
    deleteScribeAudioChunk: builder.mutation<
      void,
      { scribeId: number; chunkId: number }
    >({
      query: ({ scribeId, chunkId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/audio-chunks/${chunkId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, arg) => [
        AUDIO_CHUNKS_TAG,
        { type: "AmbientScribes", id: arg.scribeId },
        "AmbientScribes",
        "AmbientScribeProcessingEvents"
      ]
    }),

    // Ask-AI on the custom context. Mirrors POST /chart-notes/{id}/complete →
    // returns an HTML string. Transient (no cache tags).
    askAiOnScribeContext: builder.mutation<
      string,
      {
        scribe_id: number;
        text: string;
        user_prompt: string;
        clinic_id?: number;
      }
    >({
      query: ({ scribe_id, ...body }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/context/complete/`,
        method: "POST",
        body
      })
    }),

    getAmbientScribeTemplates: builder.query<
      AmbientScribeTemplate[],
      AmbientScribeTemplateFilters
    >({
      query: (filters) => {
        const params: Record<string, string | number | undefined> = {
          limit: filters.limit ?? 100,
          offset: filters.offset ?? 0,
          ordering: filters.ordering ?? "-pk",
          search: filters.search
        };
        const queryString = formatValidQueryParamUrlCommaSeparated(params);
        return {
          url: `/ai/ambient-scribes/templates/${queryString ? `?${queryString}` : ""}`,
          method: "GET"
        };
      },
      extraOptions: { maxRetries: 1 },
      providesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG],
      transformResponse: (
        response:
          | AmbientScribeTemplate[]
          | { results: AmbientScribeTemplate[]; count?: number }
      ) => {
        if (Array.isArray(response)) {
          return response;
        }
        return response.results ?? [];
      }
    }),

    getAmbientScribeTemplate: builder.query<AmbientScribeTemplate, number>({
      query: (id) => ({
        url: `/ai/ambient-scribes/templates/${id}/`,
        method: "GET"
      }),
      providesTags: (_result, _error, id) => [
        { type: AMBIENT_SCRIBE_TEMPLATES_TAG, id }
      ]
    }),

    createAmbientScribeTemplate: builder.mutation<
      AmbientScribeTemplate,
      AmbientScribeTemplateCreateInput
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/templates/",
        method: "POST",
        body
      }),
      invalidatesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG]
    }),

    updateAmbientScribeTemplate: builder.mutation<
      AmbientScribeTemplate,
      AmbientScribeTemplateUpdateInput & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/ai/ambient-scribes/templates/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG]
    }),

    deleteAmbientScribeTemplate: builder.mutation<void, number>({
      query: (id) => ({
        url: `/ai/ambient-scribes/templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG]
    }),

    reverseEngineerAmbientScribeTemplate: builder.mutation<
      { template: string },
      { text: string; format?: PromptOutputFormat }
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/templates/reverse-engineering/",
        method: "POST",
        body
      })
    }),

    getAmbientScribeProcessingContext: builder.query<
      AmbientScribeProcessingContext,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/ai/ambient-scribes/${id}/processing-context/`
      }),
      transformResponse: (response: AmbientScribeProcessingContextApi) =>
        transformAmbientScribeProcessingContextResponse(response),
      providesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.id },
        "AmbientScribeProcessingEvents"
      ]
    }),

    getProcessingEvents: builder.query<
      GetProcessingEventsResponse,
      GetProcessingEventsQueryArg
    >({
      query: ({
        scribeId,
        offset = 0,
        limit = 25
      }: GetProcessingEventsQueryArg) => ({
        url: `/ai/ambient-scribes/${scribeId}/processing-events/?offset=${offset}&limit=${limit}`
      }),
      transformResponse: (
        response: GetProcessingEventsApiPayload,
        _meta: unknown,
        arg: GetProcessingEventsQueryArg
      ) => normalizeProcessingEventsResponse(response, arg),
      providesTags: ["AmbientScribeProcessingEvents"]
    })
  })
});

export const {
  useGetAmbientScribeQuery,
  useGetAmbientScribesQuery,
  useGetAmbientScribesPaginatedQuery,
  useGetAmbientScribeFeedbackQuery,
  useCreateAmbientScribeMutation,
  useUpdateAmbientScribeMutation,
  useDeleteAmbientScribeMutation,
  useGenerateAmbientSummaryMutation,
  useGenerateAmbientSoapNotesMutation,
  useGenerateAmbientScribeAsyncMutation,
  useGenerateTranscriptionMutation,
  useSubmitAmbientScribeFeedbackMutation,
  useFinalizeStuckScribeMutation,
  usePauseAmbientScribeMutation,
  useGetScribeRecordingSessionsQuery,
  useDeleteScribeRecordingSessionMutation,
  useEditAmbientScribeMutation,
  useCompleteAmbientScribeMutation,
  useGetScribeAudioChunksQuery,
  useDeleteScribeAudioChunkMutation,
  useAskAiOnScribeContextMutation,
  useGetAmbientScribeTemplatesQuery,
  useGetAmbientScribeTemplateQuery,
  useCreateAmbientScribeTemplateMutation,
  useUpdateAmbientScribeTemplateMutation,
  useDeleteAmbientScribeTemplateMutation,
  useReverseEngineerAmbientScribeTemplateMutation,
  useGetAmbientScribeProcessingContextQuery,
  useGetProcessingEventsQuery
} = ambientScribeApi;
